import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import {
  IconAlertTriangle,
  IconAlertTriangleFilled,
} from '@tabler/icons-react';
import { SearchInput } from '../../../components/SearchInput';
import { UnifyTable, type UnifyTableColumn } from '../../../components/UnifyTable';
import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { usePermissions } from '../../../contexts/PermissionContext';
import {
  type Client,
  type ClientGroup,
  getClientDisplayName,
  getEpisodeStatusColor,
  formatEpisodeStatusLabel,
  formatDate,
  getEpisodeRows,
  episodeRowMatchesProviderFilter,
  providerFilterOptionsFromRows,
  defaultProviderFilterForRole,
  resolveAllByDefaultFilter,
  PROVIDER_FILTER_MY_CLIENTS,
  PROVIDER_FILTER_MY_TEAM,
} from '../data';
import { TransferModal, ManageEpisodeModal, CaseloadTabPanelLayout, type TransferModalContext } from '../components';
import { CaseloadRowActionsMenu } from '../components/CaseloadRowActionsMenu';
import type { GroupMember, RelatedPerson, EpisodeManageUpdates, GroupTherapyCaseloadAction } from '../components';
import type { DischargeSummary } from '../../clients/data';
import { mockRelationships } from '../../../data/clientChartApi';
import type { EpisodeOfCareRow } from '../data';
import { ensureClientMessageThread } from '../../messaging/services/clientMessageChat';
import { useClients } from '../../../contexts/ClientsContext';
import {
  unenrollPatientFromGroup,
  returnPatientToGroup,
  type GroupParticipationEndMode,
} from '@/lib/groupTherapy';
import { loadPractitionerPreferredLabelMap } from '@/lib/loadPractitionerPreferredLabels';
import {
  getGroupFacilitators,
  getManagingOrganizationId,
} from '@unify/fhir-model/resources/group';
import type { ClientEpisode } from '../types';
import { toFhirDateOnlyLocal } from '@/lib/dateHelpers';

function isGroupTherapyEpisode(episode: ClientEpisode, groupType?: string): boolean {
  if (episode.groupCohortReference) return true;
  if (groupType === 'Group Therapy') return true;
  return episode.name === 'Group Therapy';
}

interface AllTabProps {
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  clientGroups: ClientGroup[];
  onDischarge?: (clientId: string, summary: DischargeSummary) => void;
}

/**
 * All clients tab
 * Displays all clients tied to your provider account with filtering and sorting
 */
export function AllTab({ clients, setClients, clientGroups, onDischarge }: AllTabProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { practitioner: viewerPractitioner } = useCurrentUserPractitioner();
  const { role } = usePermissions();
  const { reloadCaseload } = useClients();
  const [searchQuery, setSearchQuery] = useState('');
  /** null = untouched, so clients/episode types that load later stay included. */
  const [clientSelection, setClientSelection] = useState<string[] | null>(null);
  /** null = untouched. Resolved per render so the role default still applies once RBAC loads. */
  const [providerSelection, setProviderSelection] = useState<string[] | null>(null);
  const providerFilter = providerSelection ?? defaultProviderFilterForRole(role);
  const [episodeTypeSelection, setEpisodeTypeSelection] = useState<string[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>(() =>
    ['active', 'planned', 'waitlist', 'follow-up', 'on-hold']
  );

  // Transfer modal state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferModalContext, setTransferModalContext] = useState<TransferModalContext | null>(null);

  // Persist episode-party overrides (primary patient, guarantor) changed via ManageEpisodeModal
  type EpisodeOverride = {
    primaryClinicalPatientId?: string | null;
    guarantorId?: string | null;
    guarantorName?: string;
    guarantorRelationship?: string;
  };
  const [episodeOverrides, setEpisodeOverrides] = useState<Record<string, EpisodeOverride>>({});

  // ISO date set when all members in an episode have been removed; cleared when a member re-activates
  const [episodeAllInactiveSince, setEpisodeAllInactiveSince] = useState<Record<string, string>>({});

  // Deactivate modal state
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deactivateClient, setDeactivateClient] = useState<Client | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');

  // Manage group episode modal state
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [manageRow, setManageRow] = useState<EpisodeOfCareRow | null>(null);

  const statusOptions = useMemo(
    () => [
      { value: 'active', label: 'Active' },
      { value: 'planned', label: 'Planned' },
      { value: 'waitlist', label: 'Waitlist' },
      { value: 'follow-up', label: 'Follow up' },
      { value: 'on-hold', label: 'On Hold' },
      { value: 'finished', label: 'Finished' },
    ],
    []
  );

  // Build episode-based rows from clients
  const allEpisodeRows = useMemo(() => getEpisodeRows(clients, clientGroups), [clients, clientGroups]);

  // Provider filter options: My Clients, My Team, then the role-scoped named providers.
  const providerOptions = useMemo(
    () => [
      { value: PROVIDER_FILTER_MY_CLIENTS, label: 'My Clients' },
      { value: PROVIDER_FILTER_MY_TEAM, label: 'My Team' },
      ...providerFilterOptionsFromRows(allEpisodeRows, viewerPractitioner?.id),
    ],
    [allEpisodeRows, viewerPractitioner?.id],
  );

  const episodeTypeOptions = useMemo(() => {
    const names = new Set(allEpisodeRows.map((r) => r.episode.name));
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [allEpisodeRows]);

  // Client filter options (for "filter by client" dropdown)
  const clientFilterOptions = useMemo(
    () =>
      clients.map((c) => ({
        value: c.id,
        label: getClientDisplayName(c),
      })),
    [clients]
  );

  const clientFilter = resolveAllByDefaultFilter(clientSelection, clientFilterOptions);
  const episodeTypeFilter = resolveAllByDefaultFilter(episodeTypeSelection, episodeTypeOptions);

  // Filter and sort episode rows
  const filteredRows = useMemo(() => {
    let rows = allEpisodeRows;

    // Empty FilterMultiSelect = no rows (not "all").
    if (clientFilter.length === 0) {
      rows = [];
    } else {
      rows = rows.filter((r) => {
        if (r.clients) return r.clients.some((c) => clientFilter.includes(c.id));
        return clientFilter.includes(r.client.id);
      });
    }

    // Provider filter — per **episode row** care manager (`episode.provider`), not client-level `supervisee`.
    rows = rows.filter((r) =>
      episodeRowMatchesProviderFilter(r, providerFilter, {
        viewerPractitionerId: viewerPractitioner?.id,
      }),
    );

    // Search filter (client name, email, phone) — check all members for merged rows
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      rows = rows.filter((r) => {
        if (r.displayName?.toLowerCase().includes(query)) return true;
        const members = r.clients ?? [r.client];
        return members.some(
          (c) =>
            getClientDisplayName(c).toLowerCase().includes(query) ||
            c.email?.toLowerCase().includes(query) ||
            c.phone?.toLowerCase().includes(query)
        );
      });
    }

    if (episodeTypeFilter.length === 0) {
      rows = [];
    } else {
      rows = rows.filter((r) => episodeTypeFilter.includes(r.episode.name));
    }

    if (statusFilter.length === 0) {
      rows = [];
    } else {
      rows = rows.filter((r) => statusFilter.includes(r.episode.status));
    }

    return rows;
  }, [
    allEpisodeRows,
    clientFilter,
    searchQuery,
    providerFilter,
    viewerPractitioner?.id,
    episodeTypeFilter,
    statusFilter,
  ]);

  const handleClientClick = (clientId: string) => {
    navigate(`/dashboard/clients/${clientId}`);
  };

  /** Navigate to client Medical Record with Episode of Care filter set to this episode. */
  const getMedicalRecordUrlForEpisode = (clientId: string, episodeId: string) =>
    `/dashboard/clients/${clientId}?tab=chart&episodeId=${encodeURIComponent(episodeId)}`;

  const handleTransferSuccess = () => {
    setTransferModalOpen(false);
    setTransferModalContext(null);
  };

  const buildTransferContext = (row: EpisodeOfCareRow): TransferModalContext | null => {
    const client = row.client;
    const episodes = client.episodes ?? [];
    if (episodes.length === 0) {
      return {
        patientId: client.id,
        patientDisplay: row.displayName ?? getClientDisplayName(client),
        episodeOfCareId: row.episode.id,
        episodeDisplay: row.episode.name,
        serviceRequestId: '',
        currentProviderDisplay: row.episode.provider ?? client.primaryProvider ?? client.provider,
        serviceTypeDisplay: client.clientType,
        episodeOptions: [
          {
            value: row.episode.id,
            label: row.episode.name,
            currentProviderDisplay: row.episode.provider ?? client.primaryProvider ?? client.provider,
            serviceTypeDisplay: client.clientType,
          },
        ],
      };
    }

    const episodeOptions = episodes.map((episode) => ({
      value: episode.id,
      label: episode.name,
      currentProviderDisplay: episode.provider ?? client.primaryProvider ?? client.provider,
      serviceTypeDisplay: client.clientType,
    }));

    const defaultEpisode =
      episodes.find((episode) => episode.id === row.episode.id) ?? episodes[0];

    return {
      patientId: client.id,
      patientDisplay: row.displayName ?? getClientDisplayName(client),
      episodeOfCareId: defaultEpisode.id,
      episodeDisplay: defaultEpisode.name,
      serviceRequestId: '',
      currentProviderDisplay:
        defaultEpisode.provider ?? client.primaryProvider ?? client.provider,
      serviceTypeDisplay: client.clientType,
      episodeOptions,
    };
  };

  const handleTransfer = (row: EpisodeOfCareRow, e: React.MouseEvent) => {
    e.stopPropagation();
    const context = buildTransferContext(row);
    if (!context) {
      notifications.show({
        title: 'Unable to transfer',
        message: 'No episode of care is available for this client.',
        color: 'red',
      });
      return;
    }
    setTransferModalContext(context);
    setTransferModalOpen(true);
  };

  const handleMessage = async (row: EpisodeOfCareRow, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!profile) {
      notifications.show({
        title: 'Unable to open messages',
        message: 'Your user profile could not be loaded.',
        color: 'red',
      });
      return;
    }

    const patientDisplay = row.displayName ?? getClientDisplayName(row.client);
    try {
      const threadId = await ensureClientMessageThread(
        medplum,
        profile,
        row.client.id,
        patientDisplay,
      );
      navigate(`/dashboard/messages/${threadId}`);
    } catch (err) {
      notifications.show({
        title: 'Unable to open messages',
        message: err instanceof Error ? err.message : 'An unexpected error occurred',
        color: 'red',
      });
    }
  };

  /** Build modal props from any episode row — individual or group. */
  const getManageModalProps = (row: EpisodeOfCareRow) => {
    const isGroupRow = (row.clients?.length ?? 0) > 1;
    const override = episodeOverrides[row.episode.id] ?? {};

    if (!isGroupRow) {
      const client = row.client;
      const relatedPersons: RelatedPerson[] = (client.linkedAccounts ?? []).map((la, idx) => ({
        id: `la-${client.id}-${idx}`,
        name: la.name ?? '',
        relationship: la.role,
        phone: la.phone,
        linkedToMemberId: client.id,
        linkedToMemberName: getClientDisplayName(client),
      }));

      // Pass the client as the single member so the remove-member workflow is available
      const singleMember: GroupMember[] = episodeAllInactiveSince[row.episode.id]
        ? []
        : [{ id: client.id, name: getClientDisplayName(client), phone: client.phone }];

      const isGroupTherapy = isGroupTherapyEpisode(row.episode);

      return {
        isGroupRow: false,
        isGroupTherapy,
        members: singleMember,
        clientName: getClientDisplayName(client),
        allowPrimaryPatientChange: false as const,
        relatedPersons,
        groupType: isGroupTherapy ? 'Group Therapy' : (undefined as string | undefined),
        groupCohortName: row.episode.groupCohortName,
        episodeStatus: row.episode.status,
        primaryClinicalPatientId: client.id,
        guarantorId: override.guarantorId !== undefined ? override.guarantorId : null,
        guarantorName: override.guarantorName ?? '',
        guarantorRelationship: override.guarantorRelationship ?? '',
        allInactiveSince: episodeAllInactiveSince[row.episode.id],
      };
    }

    const rowClientIds = (row.clients ?? []).map((c) => c.id);

    const group = clientGroups.find((g) =>
      g.members.some((m) => rowClientIds.includes(m.clientId)),
    );

    const relationship = mockRelationships.find((r) =>
      rowClientIds.some((id) => r.memberPatientIds.includes(id)),
    );

    const members: GroupMember[] = (row.clients ?? []).map((c) => {
      const groupMember = group?.members.find((m) => m.clientId === c.id);
      return {
        id: c.id,
        name: getClientDisplayName(c),
        relationship: groupMember?.role,
        phone: c.phone,
      };
    });

    // Remove members that have already been discharged (all-inactive state)
    const activeMembers = episodeAllInactiveSince[row.episode.id] ? [] : members;
    const isGroupTherapy = isGroupTherapyEpisode(row.episode, group?.type);

    return {
      isGroupRow: true,
      isGroupTherapy,
      members: activeMembers,
      clientName: undefined as string | undefined,
      allowPrimaryPatientChange: !isGroupTherapy as boolean,
      relatedPersons: [] as RelatedPerson[],
      groupType: group?.type ?? (isGroupTherapy ? 'Group Therapy' : undefined),
      groupCohortName: row.episode.groupCohortName,
      episodeStatus: row.episode.status,
      primaryClinicalPatientId:
        override.primaryClinicalPatientId !== undefined
          ? override.primaryClinicalPatientId
          : (relationship?.primaryClinicalMemberId ?? null),
      guarantorId:
        override.guarantorId !== undefined
          ? override.guarantorId
          : (relationship?.guarantor.id ?? null),
      guarantorName:
        override.guarantorName !== undefined
          ? override.guarantorName
          : (relationship?.guarantor.name ?? ''),
      guarantorRelationship:
        override.guarantorRelationship !== undefined
          ? override.guarantorRelationship
          : (relationship?.guarantor.relationship ?? ''),
      allInactiveSince: episodeAllInactiveSince[row.episode.id],
    };
  };

  const handleManageGroupSave = (updates: EpisodeManageUpdates) => {
    if (!manageRow) return;

    // Persist overrides in local state so re-opening the modal shows updated values
    setEpisodeOverrides((prev) => ({
      ...prev,
      [manageRow.episode.id]: {
        primaryClinicalPatientId: updates.primaryClinicalPatientId,
        guarantorId: updates.guarantorId,
        guarantorName: updates.guarantorName,
        guarantorRelationship: updates.guarantorRelationship,
      },
    }));

    notifications.show({
      title: 'Episode updated',
      message: updates.primaryClinicalPatientId
        ? 'Primary clinical patient and episode settings updated.'
        : 'Episode settings updated.',
      color: 'green',
    });
    setManageModalOpen(false);
    setManageRow(null);
  };

  const resolveGroupIdFromEpisode = (episode: ClientEpisode): string | undefined => {
    const ref = episode.groupCohortReference;
    if (ref?.startsWith('Group/')) return ref.slice('Group/'.length);
    return undefined;
  };

  const handleGroupTherapyAction = async (
    action: GroupTherapyCaseloadAction,
    memberId: string,
    summary?: DischargeSummary,
  ) => {
    if (!manageRow) return;

    const groupId = resolveGroupIdFromEpisode(manageRow.episode);
    if (!groupId) {
      notifications.show({
        title: 'Group not found',
        message:
          'Cannot resolve the Group resource from this episode. The EpisodeOfCare is missing a group-reference / episodeofcare-group extension.',
        color: 'red',
      });
      throw new Error('Missing group cohort reference on episode');
    }

    const memberClient = (manageRow.clients ?? [manageRow.client]).find((c) => c.id === memberId);
    const memberName = memberClient ? getClientDisplayName(memberClient) : 'Client';

    try {
      const group = await medplum.readResource('Group', groupId);
      if (action === 'return') {
        const facilitatorRefs = getGroupFacilitators(group);
        const facilitatorIds = facilitatorRefs
          .map((r) => r.reference?.replace(/^Practitioner\//, ''))
          .filter((id): id is string => !!id);
        const facilitatorLabels = await loadPractitionerPreferredLabelMap(
          medplum,
          facilitatorIds,
        );
        await returnPatientToGroup({
          medplum,
          group,
          patientId: memberId,
          facilitatorIds,
          facilitatorDisplays: facilitatorIds.map((id) => facilitatorLabels.get(id) ?? ''),
          organizationId: getManagingOrganizationId(group),
        });
        notifications.show({
          title: 'Returned to group',
          message: `${memberName} has been returned to the group and the episode is active again.`,
          color: 'green',
        });
      } else {
        const mode = action as GroupParticipationEndMode;
        await unenrollPatientFromGroup({
          medplum,
          group,
          patientId: memberId,
          mode,
        });
        if (mode === 'formal-discharge' && summary) {
          onDischarge?.(memberId, { ...summary, episodeId: manageRow.episode.id });
        }
        const labels: Record<GroupParticipationEndMode, string> = {
          suspend: 'suspended (on hold)',
          'formal-discharge': 'formally discharged',
          'pre-start-withdraw': 'withdrawn before start',
        };
        notifications.show({
          title: 'Group participation updated',
          message: `${memberName} has been ${labels[mode]}.`,
          color: 'green',
        });
      }

      reloadCaseload();
      setManageModalOpen(false);
      setManageRow(null);
    } catch (err) {
      notifications.show({
        title: 'Group therapy action failed',
        message: err instanceof Error ? err.message : 'An unexpected error occurred',
        color: 'red',
      });
      throw err;
    }
  };

  const handleManageMemberRemove = (memberId: string, summary: DischargeSummary) => {
    if (!manageRow) return;

    // Group Therapy rows should use FHIR-backed actions; fall back only if mis-wired.
    if (isGroupTherapyEpisode(manageRow.episode)) {
      void handleGroupTherapyAction('formal-discharge', memberId, summary);
      return;
    }

    const allRowClients = manageRow.clients ?? [manageRow.client];
    const memberClient = allRowClients.find((c) => c.id === memberId);
    const memberName = memberClient ? getClientDisplayName(memberClient) : 'Member';

    setClients((prevClients) =>
      prevClients.map((c) => {
        if (c.id !== memberId) return c;
        const updatedEpisodes = c.episodes?.map((ep) =>
          ep.id === manageRow.episode.id
            ? { ...ep, status: 'finished' as const, endDate: toFhirDateOnlyLocal(new Date()) }
            : ep,
        );
        return { ...c, status: 'Discharged' as Client['status'], episodes: updatedEpisodes };
      }),
    );

    onDischarge?.(memberId, { ...summary, episodeId: manageRow.episode.id });

    // Check if this was the last active member in the episode row
    const remainingActiveIds = allRowClients
      .filter((c) => c.id !== memberId)
      .map((c) => c.id);
    if (remainingActiveIds.length === 0) {
      setEpisodeAllInactiveSince((prev) => ({
        ...prev,
        [manageRow.episode.id]: toFhirDateOnlyLocal(new Date()),
      }));
    }

    notifications.show({
      title: 'Member removed',
      message: `${memberName} has been discharged from the episode.`,
      color: 'green',
    });
  };


  const handleDeactivateSubmit = () => {
    if (!deactivateClient || !deactivateReason.trim()) {
      return;
    }

    // Update client status to "Inactive"
    setClients((prevClients) =>
      prevClients.map((c) =>
        c.id === deactivateClient.id
          ? {
            ...c,
            status: 'Inactive' as Client['status'],
          }
          : c
      )
    );

    // Reset and close modal
    setDeactivateModalOpen(false);
    setDeactivateClient(null);
    setDeactivateReason('');
  };

  const columns = useMemo(
    (): UnifyTableColumn<EpisodeOfCareRow>[] => [
      {
        key: 'name',
        label: 'Client Name',
        sortable: true,
        getValue: (row) => row.displayName ?? getClientDisplayName(row.client),
        render: (row) => (
          <Group gap={6} wrap="nowrap">
            <Anchor
              component={Link}
              to={`/dashboard/clients/${row.client.id}`}
              size="sm"
              fw={500}
              c="gold.7"
              underline="hover"
              style={{ textUnderlineOffset: 2, maxWidth: 220 }}
              truncate="end"
              onClick={(e) => e.stopPropagation()}
              title={row.displayName ?? getClientDisplayName(row.client)}
            >
              {row.displayName ?? getClientDisplayName(row.client)}
            </Anchor>
            {row.client.relationshipType === 'Supervisee' && (
              <Badge size="xs" variant="light" color="blue">Supervisee</Badge>
            )}
            {!row.clients &&
              ((row.client.highRiskFlags && row.client.highRiskFlags.length > 0) ||
                (row.client.riskTags && row.client.riskTags.length > 0)) && (
              <Badge color="red" variant="filled" leftSection={<IconAlertTriangle size={12} />}>
                High Risk
              </Badge>
            )}
          </Group>
        ),
      },
      {
        key: 'episode',
        label: 'Episode of Care',
        sortable: true,
        getValue: (row) => row.episode.name,
        render: (row) => (
          <Stack gap={2}>
            <Anchor
              component={Link}
              to={getMedicalRecordUrlForEpisode(row.client.id, row.episode.id)}
              size="sm"
              c="gold.7"
              style={{ textUnderlineOffset: 2 }}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
            >
              {row.episode.name}
            </Anchor>
            {row.episode.groupCohortName ? (
              <Text size="xs" c="dimmed" truncate="end" maw={220} title={row.episode.groupCohortName}>
                {row.episode.groupCohortName}
              </Text>
            ) : null}
          </Stack>
        ),
      },
      {
        key: 'firstSession',
        label: 'First Session',
        sortable: true,
        getValue: (row) => row.episode.startDate,
        render: (row) => <Text size="sm">{formatDate(row.episode.startDate)}</Text>,
      },
      {
        key: 'mostRecent',
        label: 'Most Recent Session',
        sortable: true,
        getValue: (row) => row.client.mostRecentAppointment ?? '',
        render: (row) => <Text size="sm">{formatDate(row.client.mostRecentAppointment)}</Text>,
      },
      {
        key: 'clientType',
        label: 'Client Type',
        sortable: true,
        getValue: (row) => row.client.clientType,
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        getValue: (row) => formatEpisodeStatusLabel(row.episode.status),
        render: (row) => (
          <Badge color={getEpisodeStatusColor(row.episode.status)} variant="light">
            {formatEpisodeStatusLabel(row.episode.status)}
          </Badge>
        ),
      },
      {
        key: 'actions',
        label: '',
        sortable: false,
        pinRight: true,
        width: 48,
        render: (row) => (
          <CaseloadRowActionsMenu
            onTransfer={(e) => handleTransfer(row, e)}
            onMessage={(e) => {
              void handleMessage(row, e);
            }}
          />
        ),
      },
    ],
    [medplum, navigate, profile],
  );

  return (
    <>
      <CaseloadTabPanelLayout
        filters={
          <>
            <SearchInput
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />
            <FilterMultiSelect
              allLabel="All Clients"
              data={clientFilterOptions}
              value={clientFilter}
              onChange={setClientSelection}
              selectedLabel="Selected"
              searchPlaceholder="Search client..."
            />
            <FilterMultiSelect
              allLabel="All Providers"
              data={providerOptions}
              value={providerFilter}
              onChange={setProviderSelection}
              selectedLabel="Selected"
              optionsAboveDivider={['my-clients', 'supervisee-caseload']}
            />
            <FilterMultiSelect
              allLabel="All Episodes"
              data={episodeTypeOptions}
              value={episodeTypeFilter}
              onChange={setEpisodeTypeSelection}
            />
            <FilterMultiSelect
              allLabel="All Statuses"
              data={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </>
        }
      >
        <UnifyTable<EpisodeOfCareRow>
          withPaper={false}
          searchable={false}
          columns={columns}
          data={filteredRows}
          getRowKey={(row) => `${row.client.id}-${row.episode.id}`}
          sortable
          pagination
          defaultRowsPerPage={50}
          emptyMessage="No episodes of care found matching your criteria"
          onRowClick={(row) => handleClientClick(row.client.id)}
          tableProps={{ highlightOnHover: true }}
        />
      </CaseloadTabPanelLayout>

      <TransferModal
        opened={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setTransferModalContext(null);
        }}
        context={transferModalContext}
        onSuccess={handleTransferSuccess}
      />

      {/* Manage Episode Modal — handles all episode types (individual + group) */}
      {manageRow && (() => {
        const props = getManageModalProps(manageRow);
        return (
          <ManageEpisodeModal
            opened={manageModalOpen}
            onClose={() => {
              setManageModalOpen(false);
              setManageRow(null);
            }}
            episodeName={manageRow.episode.name}
            groupType={props.groupType}
            members={props.members}
            clientName={props.clientName}
            allowPrimaryPatientChange={props.allowPrimaryPatientChange}
            relatedPersons={props.relatedPersons}
            primaryClinicalPatientId={props.primaryClinicalPatientId}
            guarantorId={props.guarantorId}
            guarantorName={props.guarantorName}
            guarantorRelationship={props.guarantorRelationship}
            allInactiveSince={props.allInactiveSince}
            isGroupTherapy={props.isGroupTherapy}
            episodeStatus={props.episodeStatus}
            groupCohortName={props.groupCohortName}
            onSave={handleManageGroupSave}
            onMemberRemove={handleManageMemberRemove}
            onGroupTherapyAction={props.isGroupTherapy ? handleGroupTherapyAction : undefined}
          />
        );
      })()}

      {/* Deactivate Modal */}
      <Modal
        opened={deactivateModalOpen}
        onClose={() => {
          setDeactivateModalOpen(false);
          setDeactivateClient(null);
          setDeactivateReason('');
        }}
        title="Deactivate Client"
        size="md"
        centered
      >
        {deactivateClient && (
          <Stack gap="md">
            <Alert
              icon={<IconAlertTriangleFilled size={16} />}
              title="Confirm Deactivation"
              color="yellow"
            >
              Are you sure you want to deactivate{' '}
              <strong>{getClientDisplayName(deactivateClient)}</strong>? This action will change
              their status to Inactive.
            </Alert>

            <Textarea
              label="Deactivation Reason"
              placeholder="Enter reason for deactivation..."
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.currentTarget.value)}
              minRows={4}
              required
            />

            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={() => {
                  setDeactivateModalOpen(false);
                  setDeactivateClient(null);
                  setDeactivateReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={handleDeactivateSubmit}
                disabled={!deactivateReason.trim()}
              >
                Confirm Deactivation
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
